import { Button, Box, Typography, Paper, Zoom, IconButton, Stack, Chip, Menu, MenuItem, Checkbox, Badge, Tooltip } from '@mui/material';
import React, { useState, useEffect } from 'react';
import CreateNote from '../pages/Invoice/CreateNote';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CustomModal from './CustomModal';
import { Delete, Edit, NoteAlt, Link, LinkOff, FilePresent } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import UserSelect from './UserSelect';
import API from '../services/api';
import CustomSnackBar from './CustomSnackBar';               // <-- agregado
import { SettlementsAPI } from '../services/api';            // <-- ya lo usas en otros forms

const formatDateLabel = (d) => {
  if (!d) return 'Sin fecha';
  if (typeof d === 'string') return d;
  if (typeof d === 'object') {
    if (d.date) return d.date;
    try {
      const dt = new Date(d);
      if (!Number.isNaN(dt.getTime())) return dt.toISOString().split('T')[0];
    } catch (e) { }
  }
  return String(d);
};

function ButtonNotes({
  notes,
  setNotes,
  setInvoice,
  invoice,
  validateUnique = false,
  selectedInvoices = [],
  logicalWarehouses = [],
  canUseLogical = false,
}) {
  const [open, setOpen] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState(null);
  const [allNotes, setAllNotes] = useState([]);
  const [recentNotes, setRecentNotes] = useState([]);
  const [visibleCount, setVisibleCount] = useState(5);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuIndex, setMenuIndex] = useState(null);

  // Snack para mensajes
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
  const getTs = (n) => {
    const s = n?.created_at;               // << único campo de fecha
    if (!s) return 0;
    const t = new Date(s).getTime();
    return Number.isFinite(t) ? t : 0;
  };

  const resolveLogical = (note) => {
    if (note?.logical_warehouse) return note.logical_warehouse;
    const id = note?.logical_warehouse_id ?? null;
    if (id == null) return null;
    return (logicalWarehouses || []).find((l) => String(l.id) === String(id)) || null;
  };
  const getWarehouseLabel = (note) => {
    const logical = resolveLogical(note);
    const logicalName = logical?.name || '';
    const legacy = note?.legacy_warehouse_text || '';

    if (!canUseLogical) {
      return 'No aplica';
    }
    if (logicalName) {
      return logicalName;
    }
    if (legacy || note?.warehouse) {
      const text = legacy || note.warehouse;
      return `Legacy: ${text}`;
    }
    return 'Sin bodega';
  };

  useEffect(() => {
    const loadAll = async () => {
      try {
        const noteRes = await API.get('receive-note/', { params: { ordering: '-created_at' } });
        const ordered = [...(noteRes.data ?? [])].sort((a, b) => getTs(b) - getTs(a));
        setAllNotes(ordered);
      } catch (err) {
        console.error('Error cargando notas:', err);
        setAllNotes([]);
      }
    };
    loadAll();
  }, []);


  useEffect(() => {
    const currentIds = new Set(notes.map(n => n.id).filter(Boolean));
    const filtered = allNotes.filter(n => !currentIds.has(n.id));
    const ordered = filtered.sort((a, b) => getTs(b) - getTs(a));
    setRecentNotes(ordered.slice(0, visibleCount));
  }, [allNotes, notes, visibleCount]);


  // --- Helper: valida en backend que el número no exista ---
  const checkReceiveNoteUnique = async (number) => {
    const num = String(number || '').trim();
    if (!num) {
      setSnack({ open: true, message: 'Número de nota de entrada requerido.', severity: 'error' });
      return false;
    }
    try {
      await SettlementsAPI.checkReceiveNote(num); // 200 = OK
      return true;
    } catch (e) {
      if (e?.response?.status === 409) {
        setSnack({ open: true, message: 'Error por nota de entrada duplicada: ese número ya existe.', severity: 'error' });
      } else {
        setSnack({ open: true, message: 'No se pudo validar la nota de entrada. Intenta de nuevo.', severity: 'error' });
      }
      return false;
    }
  };

  // --- Helper: valida unicidad local si lo pides con validateUnique ---
  const localDuplicateExists = (num) => {
    const n = String(num || '').trim();
    if (!n) return false;
    const inSelected = notes.some(x => String(x.number).trim() === n);
    const inRecent = recentNotes.some(x => String(x.number).trim() === n);
    return inSelected || inRecent;
  };

  // Agregar nueva (desde modal)
  const handleAddNote = async (note) => {
    const num = note?.number;
    if (validateUnique && localDuplicateExists(num)) {
      setSnack({ open: true, message: 'Ese número ya está en la selección.', severity: 'error' });
      return;
    }
    const ok = await checkReceiveNoteUnique(num);
    if (!ok) return; // no cerrar modal ni agregar

    const defaultLinks = selectedInvoices.length === 1 ? [selectedInvoices[0].id] : [];
    const newNote = { ...note, isNew: true, _shine: true, linkedInvoices: defaultLinks };
    setNotes(prev => [...prev, newNote]);
    setOpen(false);
    setTimeout(() => {
      setNotes(prev => prev.map(n => (n === newNote ? { ...n, _shine: false } : n)));
    }, 1200);
  };

  // Guardar cambios de una existente en edición (nueva en sesión)
  const handleSaveNote = async (index, updatedNote) => {
    const num = updatedNote?.number;
    if (validateUnique) {
      // Evita choque con otras del arreglo (excluye la propia en edición)
      const dup = notes.some((x, i) => i !== index && String(x.number).trim() === String(num || '').trim());
      if (dup) {
        setSnack({ open: true, message: 'Ese número ya está en la selección.', severity: 'error' });
        return;
      }
    }
    const ok = await checkReceiveNoteUnique(num);
    if (!ok) return;

    setNotes(prev =>
      prev.map((note, i) =>
        i === index ? { ...updatedNote, isNew: note.isNew, _shine: false, linkedInvoices: note.linkedInvoices || [] } : note
      )
    );
    setNoteToEdit(null);
    setOpen(false);
  };

  const handleDeleteNote = (indexToDelete) => {
    setNotes(prev => prev.filter((_, idx) => idx !== indexToDelete));
  };

  const handleLinkExisting = (note) => {
    const defaultLinks = selectedInvoices.length === 1 ? [selectedInvoices[0].id] : [];
    setNotes(prev => [...prev, { ...note, isNew: false, _shine: false, linkedInvoices: defaultLinks }]);
    setRecentNotes(prev => prev.filter(n => n.id !== note.id));
  };

  const handleUnlinkNote = (index) => {
    const removed = notes[index];
    setNotes(prev => prev.filter((_, i) => i !== index));
    if (removed && removed.id) {
      setRecentNotes(prev => {
        const next = [removed, ...prev];
        const seen = new Set();
        const unique = [];
        for (const n of next) {
          if (!seen.has(n.id)) { seen.add(n.id); unique.push(n); }
          if (unique.length === 5) break;
        }
        return unique;
      });
    }
  };

  const handleEditNote = (indexToEdit) => {
    const note = notes[indexToEdit];
    setNoteToEdit({ ...note, index: indexToEdit });
    setOpen(true);
  };

  const handleOpenMenu = (event, index) => {
    setAnchorEl(event.currentTarget);
    setMenuIndex(index);
  };

  const handleCloseMenu = () => {
    if (menuIndex !== null) {
      const note = notes[menuIndex];
      if (!note.linkedInvoices || note.linkedInvoices.length === 0) {
        handleUnlinkNote(menuIndex);
      }
    }
    setAnchorEl(null);
    setMenuIndex(null);
  };

  const handleToggleInvoice = (invoiceId) => {
    if (menuIndex === null) return;
    setNotes(prev =>
      prev.map((note, idx) => {
        if (idx !== menuIndex) return note;
        const current = note.linkedInvoices || [];
        const exists = current.includes(invoiceId);
        const next = exists ? current.filter(id => id !== invoiceId) : [...current, invoiceId];
        return { ...note, linkedInvoices: next };
      })
    );
  };

  const handleLoadMore = () => setVisibleCount(prev => prev + 5);

  const visibleCards = (notes?.length || 0) + (recentNotes?.length || 0);
  const totalNotes = allNotes?.length || 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '240px 1fr',
        columnGap: 2,
        alignItems: 'start',
        width: '100%',
      }}
    >
      {/* Izquierda */}
      <Stack spacing={1.5} sx={{ width: '240px' }}>
        <Button
          variant="contained"
          onClick={() => { setNoteToEdit(null); setOpen(true); }}
          startIcon={<AddCircleOutlineIcon fontSize="small" />}
          sx={{
            px: 1.2, py: 0.4, borderRadius: 2, fontWeight: 600, width: '100%',
            fontSize: '0.7rem', boxShadow: 1, textTransform: 'none',
            backgroundColor: (t) => t.palette.secondary.main,
            '&:hover': { backgroundColor: (t) => t.palette.secondary.dark || '#7c1512' },
          }}
        >
          Agregar nota
        </Button>

        <UserSelect setInvoice={setInvoice} invoice={invoice} />
      </Stack>

      {/* Derecha */}
      <Box
        sx={{
          minWidth: 0,
          overflowX: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
          flexWrap: 'nowrap',
          '::-webkit-scrollbar': { height: 6 },
          '::-webkit-scrollbar-thumb': { borderRadius: 8, backgroundColor: 'rgba(0,0,0,0.2)' },
        }}
      >
        {notes.map((note, index) => (
          <Zoom in key={`note-${index}`}>
            <Paper
              elevation={2}
              className={note._shine ? 'note-shine' : ''}
              sx={{
                px: 1.2,
                py: 0.7,
                borderRadius: 2,
                minWidth: 180,
                maxWidth: 200,
                minHeight: 80, // un poco más alta para que quepan 3 iconos
                background: 'linear-gradient(135deg, #e3f2fd, #dbeafe)',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                gridTemplateRows: 'auto auto',
                columnGap: 0.75,
                rowGap: 0.25,
                alignItems: 'center',
                position: 'relative',
                '&:hover': { boxShadow: 6 },
                flexShrink: 0,
              }}
            >
              {/* Icono de archivos / vínculo (columna izquierda, centrado verticalmente) */}
              <Box
                sx={{
                  gridRow: '1 / span 2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleOpenMenu(e, index)}
                  sx={{ p: 0.5 }}
                >
                  <Badge badgeContent={note.linkedInvoices?.length || 0} color="primary">
                    <FilePresent fontSize="small" />
                  </Badge>
                </IconButton>
              </Box>

              {/* Contenido principal (número, fecha, bodega + chip New adentro, sin absolute) */}
              <Box sx={{ gridColumn: '2 / 3', gridRow: '1 / span 2', overflow: 'hidden' }}>
                <Stack spacing={0.15} alignItems="flex-start">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ width: '100%', minWidth: 0 }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      noWrap
                      sx={{ minWidth: 0 }}
                    >
                      #{note.number}
                    </Typography>
                    {note.isNew && (
                      <Chip
                        label="New"
                        size="small"
                        variant="outlined"
                        sx={{
                          bgcolor: 'rgba(192,192,192,0.15)',
                          borderColor: 'rgba(192,192,192,0.3)',
                          color: '#6b7280',
                          fontWeight: 600,
                          height: 18,
                          '& .MuiChip-label': { px: 0.6, fontSize: 10 },
                        }}
                      />
                    )}
                  </Stack>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ width: '100%' }}
                  >
                    {formatDateLabel(note.date)}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ width: '100%' }}
                  >
                    {getWarehouseLabel(note)}
                  </Typography>
                </Stack>
              </Box>

              {/* Acciones (columna derecha, vertical; ya no se salen) */}
              <Stack
                spacing={0.3}
                alignItems="center"
                sx={{ gridColumn: '3 / 4', gridRow: '1 / span 2' }}
              >
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => handleUnlinkNote(index)}
                  sx={{ p: 0.5 }}
                >
                  <LinkOff fontSize="small" />
                </IconButton>

                {note.isNew && (
                  <>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditNote(index)}
                      sx={{ p: 0.5 }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteNote(index)}
                      sx={{ p: 0.5 }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Stack>
            </Paper>
          </Zoom>
        ))}

        {recentNotes.map(note => (
          <Zoom in key={`recent-${note.id}`}>
            <Paper
              elevation={2}
              sx={{
                px: 1.2, py: 0.7, borderRadius: 2,
                minWidth: 160, maxWidth: 170, height: 70,
                background: 'linear-gradient(135deg, #f3e5f5, #e1bee7)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                transition: 'transform 0.25s ease',
                '&:hover': { transform: 'scale(1.02)', boxShadow: 6 },
                flexShrink: 0,
              }}
            >
              <Box sx={{ overflow: 'hidden', pr: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>#{note.number}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>{formatDateLabel(note.date)}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>{getWarehouseLabel(note)}</Typography>
              </Box>
              <IconButton size="small" color="primary" onClick={() => handleLinkExisting(note)} sx={{ p: 0.5 }}>
                <Link fontSize="small" />
              </IconButton>
            </Paper>
          </Zoom>
        ))}

        {allNotes.length > recentNotes.length && (
          <Tooltip title="Cargar mas notas">
            <IconButton
              onClick={handleLoadMore}
              color="primary"
              sx={{
                height: 36,
                width: 36,
                borderRadius: '50%',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                '&:hover': { backgroundColor: 'primary.light' },
                flexShrink: 0,
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Chip
          label={`${visibleCards}/${totalNotes} notas`}
          size="small"
          variant="outlined"
          sx={{
            height: 26,
            fontSize: '0.72rem',
            fontWeight: 500,
            borderRadius: 999,
            px: 1,
            backgroundColor: 'rgba(255,255,255,0.6)',
            color: 'text.secondary',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        />
      </Box>

      <CustomModal
        open={open}
        handleClose={() => setOpen(false)}
        setOpen={setOpen}
        title={noteToEdit ? 'Editar nota' : 'Ingreso de nota'}
        icon={NoteAlt}
        width="30%"
        headerColor="primary.main"
        component={CreateNote}
        componentProps={{
          onSave: handleSaveNote,         // ahora valida antes de guardar
          onAddNote: handleAddNote,       // ahora valida antes de agregar
          setOpen,
          noteToEdit,
          existingNotes: validateUnique ? [...recentNotes, ...notes] : notes,
          validateUnique,
          logicalWarehouses,
          canUseLogical,
        }}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu} PaperProps={{ sx: { minWidth: 220 } }}>
        {selectedInvoices.map(inv => (
          <MenuItem key={inv.id} onClick={() => handleToggleInvoice(inv.id)} sx={{ gap: 1 }}>
            <Checkbox checked={notes[menuIndex]?.linkedInvoices?.includes(inv.id) || false} size="small" />
            <Box>
              <Typography variant="body2" fontWeight={600}>Factura #{inv.number}</Typography>
              {inv.series && <Typography variant="caption" color="text.secondary">Serie {inv.series}</Typography>}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Snack global */}
      <CustomSnackBar
        open={snack.open}
        setOpen={(val) => setSnack(prev => ({ ...prev, open: val }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}

export default ButtonNotes;
